import { useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';

import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getRandomFileName } from '../../utility/general';

import Modal from '../common/Modal';
import PhotoModal from './PhotosModal';
import PhotoModalTop from './PhotoModalTop';

import { AspectRatio } from '@src/types.ts/video';
import { FileData } from '../../types.ts/story';
import { ImageKey, ImageWithType } from '../../types.ts/general';
import { getImageWithBlackFrameElements } from '../../utility/elements';
import applyImageOptimizations from '@src/utility/applyImageOptimizations';

type Props = {
  title?: string;
  from?: 'string';
};

const ReplacePhotoElementModal = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const [isReplacing, setIsReplacing] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>('');

  const { title = 'Swap or search for a new image', from = 'transcript' } =
    props;
  const element = videoCreator.openPhotoElementReplacementModal?.element;

  const [selectedImage, setSelectedImage] = useState<
    ImageWithType[ImageKey] | null
  >(null);

  const handleReplacePhoto = async () => {
    if (!selectedImage?.url || !element) return;

    try {
      let imageUrl = applyImageOptimizations(selectedImage.url);
      let uploadId = selectedImage.id;

      if (selectedImage.type === 'ai') {
        setIsReplacing(true);
        const altText = selectedImage.alt || 'AI generated image';
        const fileName = getRandomFileName(altText);
        const newPhotoData: FileData & { fileName: string } = {
          type: 'ai',
          url: selectedImage.url,
          fileName,
          alt: altText.slice(0, 150),
          title: altText.slice(0, 150),
        };
        const newUpload =
          await datoClientStore.assetRepository?.uploadFile(newPhotoData);
        if (newUpload) {
          imageUrl = newUpload.url;
          uploadId = newUpload.id;
          runInAction(() => {
            videoCreator.photoDataForDato = {
              ...videoCreator.photoDataForDato,
              [element.source.id]: {
                type: selectedImage.type,
                url: imageUrl,
                uploadId,
              },
            };
          });
        }
      }

      const isPortrait = +selectedImage.width < +selectedImage.height;
      const videoAspectRatio =
        videoCreator.currentVideo?.aspectRatio || AspectRatio.AR_16_9;

      const id = element.source.id;
      const modifications =
        isPortrait && videoAspectRatio === AspectRatio.AR_16_9
          ? {
              [`${id}.type`]: 'composition',
              [`${id}.source`]: null,
              [`${id}.elements`]: getImageWithBlackFrameElements({
                source: imageUrl,
              }),
            }
          : {
              [`${id}.type`]: 'image',
              [`${id}.source`]: imageUrl,
              [`${id}.fit`]: 'cover',
              [`${id}.smart_crop`]: true,
              [`${id}.elements`]: [],
            };

      await videoCreator.reframingModeManager.exitReframingMode();
      if (
        !videoCreator.hasDefaultAspectRatio() &&
        modifications[`${id}.fit`] === 'cover'
      ) {
        await videoCreator.adjustModificationsToAspectRatio(modifications, {
          ...videoCreator.getElementSource(element),
          fit: 'cover',
          type: 'image',
          source: imageUrl,
          arbor_naturalDimensions: {
            width: +selectedImage.width,
            height: +selectedImage.height,
          },
        });
      }

      await videoCreator.applyVideoStateModifications(
        modifications,
        true,
        'replacing image',
      );
      await videoCreator.setActiveElements(element.source.id);

      // Replace item in punchlist, if it exists.
      if (videoCreator.punchListManager.length()) {
        const punchListItem =
          videoCreator.punchListManager.findPunchListItemById(
            element.source.id,
          );
        if (punchListItem) {
          runInAction(() => {
            videoCreator.punchListManager.updatePunchListItem(
              element.source.id,
              {
                artifactSrc: imageUrl,
                artifactAspectRatio:
                  +selectedImage.width / +selectedImage.height,
              },
            );
          });
        }
      }
    } catch (error) {
      console.error('Error replacing photo:', error);
    } finally {
      setIsReplacing(false);
      videoCreator.openPhotoElementReplacementModal = null;
    }
  };

  if (!element || !videoCreator.isImageElement(element)) return null;

  return (
    <Modal
      isOpen={true}
      closeModal={() => (videoCreator.openPhotoElementReplacementModal = null)}
    >
      <ModalWrapper>
        <PhotoModal
          TopComponent={(props) => (
            <PhotoModalTop
              {...props}
              replaceAction={handleReplacePhoto}
              title={title}
              isSelected={!!selectedImage}
              origin={from}
              selectedImage={selectedImage}
              isLoading={isReplacing}
              setSelectedImage={setSelectedImage}
            />
          )}
          showDescription
          otherFields={
            videoCreator.isLogoElement(element)
              ? ['org_logos']
              : ['ai', 'stock', 'org_photos']
          }
          onCloseSelf={() =>
            (videoCreator.openPhotoElementReplacementModal = null)
          }
          openPrevModal={() => {}}
          selectedImageUrl={selectedImage?.url || null}
          setSelectedImage={setSelectedImage}
          from={'from'}
          searchBarRadius="10px"
        />
      </ModalWrapper>
    </Modal>
  );
});

export default ReplacePhotoElementModal;

const ModalWrapper = styled.div`
  background-color: #030419;
  color: #f3e9d7;
  width: 500px;
  box-shadow: 8px 8px 16px 0px rgba(0, 0, 0, 0.4);
  border-radius: 10px;
  padding: 20px 0;
  border: 1px solid #484848;
`;
